import { useEffect } from 'react'

type SeoProps = {
  title?: string
  description?: string
  image?: string
  canonicalUrl?: string
  type?: 'website' | 'article' | 'product' | 'service'
  jsonLd?: object | object[]
  // Travel-specific props
  pageType?: 'home' | 'about' | 'services' | 'packages' | 'package-detail' | 'blog' | 'blog-detail' | 'contact'
  packageData?: {
    id: string
    name: string
    description: string
    price?: number
    currency?: string
    destinations?: string[]
    duration?: string
    images?: string[]
    rating?: number
    reviewCount?: number
  }
  blogData?: {
    id: string
    title: string
    excerpt: string
    author?: string
    publishDate?: string
    image?: string
    tags?: string[]
  }
}

function upsertMeta(selector: { name?: string; property?: string }, content: string) {
  if (!content) return
  const head = document.head
  const attr = selector.name ? 'name' : 'property'
  const key = selector.name || selector.property!
  let el = head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink(rel: string, href: string) {
  if (!href) return
  const head = document.head
  let el = head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    head.appendChild(el)
  }
  el.setAttribute('href', href)
}

// Schema.org generators for travel business
function generateOrganizationSchema() {
  return {
    "@type": "TravelAgency",
    "@id": "https://www.mementotravel.co/#organization",
    "name": "Memento Travel Co.",
    "url": "https://www.mementotravel.co/",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.mementotravel.co/Logo%20Design%20for%20Memento%20Travel%20white.png",
      "width": 400,
      "height": 400
    },
    "description": "Professional travel agency offering personalized travel experiences, holiday packages, and destination expertise.",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "ZA"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "availableLanguage": ["English"]
    },
    "serviceArea": {
      "@type": "Place",
      "name": "Worldwide"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Travel Packages",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "TouristTrip",
            "name": "International Travel Packages"
          }
        }
      ]
    }
  }
}

function generateWebSiteSchema() {
  return {
    "@type": "WebSite",
    "@id": "https://www.mementotravel.co/#website",
    "url": "https://www.mementotravel.co/",
    "name": "Memento Travel Co.",
    "description": "Professional travel agency - Enquire as clients, Travel as our friends",
    "publisher": {
      "@id": "https://www.mementotravel.co/#organization"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.mementotravel.co/packages?query={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  }
}

function generatePackageSchema(packageData: NonNullable<SeoProps['packageData']>) {
  return {
    "@type": "TouristTrip",
    "@id": `https://www.mementotravel.co/packages/${packageData.id}`,
    "name": packageData.name,
    "description": packageData.description,
    "provider": {
      "@id": "https://www.mementotravel.co/#organization"
    },
    "url": `https://www.mementotravel.co/packages/${packageData.id}`,
    ...(packageData.images?.length && {
      "image": packageData.images.map(img => ({
        "@type": "ImageObject",
        "url": img
      }))
    }),
    ...(packageData.destinations?.length && {
      "touristDestination": packageData.destinations.map(dest => ({
        "@type": "TouristDestination",
        "name": dest
      }))
    }),
    ...(packageData.duration && {
      "duration": packageData.duration
    }),
    "offers": {
      "@type": "Offer",
      "priceCurrency": packageData.currency || "ZAR",
      ...(packageData.price && { "price": packageData.price }),
      "availability": "https://schema.org/InStock",
      "seller": {
        "@id": "https://www.mementotravel.co/#organization"
      }
    },
    ...(packageData.rating && packageData.reviewCount && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": packageData.rating,
        "reviewCount": packageData.reviewCount,
        "bestRating": 5,
        "worstRating": 1
      }
    })
  }
}

function generateBlogSchema(blogData: NonNullable<SeoProps['blogData']>, canonicalUrl: string) {
  return {
    "@type": "BlogPosting",
    "@id": canonicalUrl,
    "headline": blogData.title,
    "description": blogData.excerpt,
    "url": canonicalUrl,
    "publisher": {
      "@id": "https://www.mementotravel.co/#organization"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    ...(blogData.author && {
      "author": {
        "@type": "Person",
        "name": blogData.author
      }
    }),
    ...(blogData.publishDate && {
      "datePublished": blogData.publishDate,
      "dateModified": blogData.publishDate
    }),
    ...(blogData.image && {
      "image": {
        "@type": "ImageObject",
        "url": blogData.image
      }
    }),
    ...(blogData.tags?.length && {
      "keywords": blogData.tags.join(", ")
    })
  }
}

function generateBreadcrumbSchema(pageType: string, title?: string) {
  const items = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://www.mementotravel.co/"
    }
  ]

  switch (pageType) {
    case 'about':
      items.push({
        "@type": "ListItem",
        "position": 2,
        "name": "About",
        "item": "https://www.mementotravel.co/about"
      })
      break
    case 'services':
      items.push({
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://www.mementotravel.co/services"
      })
      break
    case 'packages':
      items.push({
        "@type": "ListItem",
        "position": 2,
        "name": "Packages",
        "item": "https://www.mementotravel.co/packages"
      })
      break
    case 'package-detail':
      items.push(
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Packages",
          "item": "https://www.mementotravel.co/packages"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": title || "Package",
          "item": window.location.href
        }
      )
      break
    case 'blog':
      items.push({
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://www.mementotravel.co/blog"
      })
      break
    case 'blog-detail':
      items.push(
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Blog",
          "item": "https://www.mementotravel.co/blog"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": title || "Article",
          "item": window.location.href
        }
      )
      break
    case 'contact':
      items.push({
        "@type": "ListItem",
        "position": 2,
        "name": "Contact",
        "item": "https://www.mementotravel.co/contact"
      })
      break
  }

  return {
    "@type": "BreadcrumbList",
    "itemListElement": items
  }
}

export default function Seo({
  title,
  description,
  image,
  canonicalUrl,
  type = 'website',
  jsonLd,
  pageType = 'home',
  packageData,
  blogData
}: SeoProps) {
  useEffect(() => {
    if (title) document.title = title
    if (description) upsertMeta({ name: 'description' }, description)

    // Canonical
    if (canonicalUrl) upsertLink('canonical', canonicalUrl)

    // Open Graph
    upsertMeta({ property: 'og:type' }, type)
    if (title) upsertMeta({ property: 'og:title' }, title)
    if (description) upsertMeta({ property: 'og:description' }, description)
    if (image) upsertMeta({ property: 'og:image' }, image)
    if (canonicalUrl) upsertMeta({ property: 'og:url' }, canonicalUrl)

    // Twitter
    if (title) upsertMeta({ name: 'twitter:title' }, title)
    if (description) upsertMeta({ name: 'twitter:description' }, description)
    if (image) upsertMeta({ name: 'twitter:image' }, image)

    // JSON-LD Schema
    const scriptId = 'seo-jsonld'
    const head = document.head
    let script = document.getElementById(scriptId) as HTMLScriptElement | null

    // Generate comprehensive schema
    const schemas: object[] = []

    // Always include base schemas
    schemas.push({
      "@context": "https://schema.org",
      "@graph": [
        generateOrganizationSchema(),
        generateWebSiteSchema()
      ]
    })

    // Add page-specific schemas
    if (pageType !== 'home') {
      schemas.push({
        "@context": "https://schema.org",
        ...generateBreadcrumbSchema(pageType, title)
      })
    }

    // Package-specific schema
    if (packageData) {
      schemas.push({
        "@context": "https://schema.org",
        ...generatePackageSchema(packageData)
      })
    }

    // Blog-specific schema
    if (blogData && canonicalUrl) {
      schemas.push({
        "@context": "https://schema.org",
        ...generateBlogSchema(blogData, canonicalUrl)
      })
    }

    // Custom JSON-LD (if provided)
    if (jsonLd) {
      const customSchemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd]
      schemas.push(...customSchemas)
    }

    // Update script
    if (schemas.length > 0) {
      if (!script) {
        script = document.createElement('script')
        script.type = 'application/ld+json'
        script.id = scriptId
        head.appendChild(script)
      }
      script.text = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas)
    } else if (script) {
      script.remove()
    }
  }, [title, description, image, canonicalUrl, type, pageType, JSON.stringify(jsonLd), JSON.stringify(packageData), JSON.stringify(blogData)])

  return null
}


